import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import AssetImage from '../../../components/AssetImage';
import type { SetupValue } from '../../data/types';
import { certificateNumber } from '../../domain/setup/setupRules';
import { money } from '../../lib/format';

type Values = Record<string, SetupValue>;

const text = (values: Values, key: string) => String(values[key] ?? '');

/** Used until a brand colour is set, so an unbranded preview carries no one's colours. */
export const NEUTRAL_PREVIEW_COLOUR = '#5F6B6D';

export function BrandingPreview({ values }: { values: Values }) {
  const colour = text(values, 'brand.colour') || NEUTRAL_PREVIEW_COLOUR;
  const wordmark = text(values, 'brand.website').split('.')[0].toUpperCase() || 'LOGO';
  const uploadedLogo = text(values, 'brand.logoUrl');
  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            bgcolor: 'background.default',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            https://{text(values, 'brand.loginUrl')}
          </Typography>
        </Box>
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
          }}
        >
          {uploadedLogo ? (
            <AssetImage
              url={uploadedLogo}
              alt="Trust logo"
              sx={{ maxWidth: 180, maxHeight: 60, objectFit: 'contain', mx: 'auto' }}
            />
          ) : (
            <Typography sx={{ fontWeight: 800, letterSpacing: '0.2em', color: colour }}>
              {wordmark}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
            Sign in to {text(values, 'fund.name')}
          </Typography>
          <Button
            variant="contained"
            size="small"
            sx={{ bgcolor: colour, '&:hover': { bgcolor: colour } }}
          >
            Sign in
          </Button>
        </Box>
      </Paper>
      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box
          sx={{
            bgcolor: text(values, 'brand.emailHeader') || colour,
            color: '#fff',
            px: 2,
            py: 1.25,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {uploadedLogo ? (
              <AssetImage
                url={uploadedLogo}
                alt="Trust logo"
                sx={{
                  maxWidth: 100,
                  maxHeight: 32,
                  verticalAlign: 'middle',
                  display: 'inline-block',
                }}
              />
            ) : (
              wordmark
            )}{' '}
            · Application received
          </Typography>
        </Box>
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Thanks for applying to {text(values, 'fund.name')}. Payment details are below.
          </Typography>
          <Button
            size="small"
            variant="contained"
            sx={{
              bgcolor: text(values, 'brand.emailButton') || colour,
              '&:hover': { bgcolor: text(values, 'brand.emailButton') || colour },
            }}
          >
            View application
          </Button>
        </Box>
      </Paper>
    </Stack>
  );
}

export function CertificatePreview({ values }: { values: Values }) {
  const partlyPaid = values['class.partlyPaid'] === true;
  const paidText = text(values, 'class.paidOnApplication');
  const paidPct = Number(paidText) || 0;
  const price = Number(values['class.issuePrice']) || 1;
  const sampleUnits = Number(values['offer.min']) / price || 100_000;
  const holderName =
    values['inv.nameCase'] === 'UPPER' ? 'SAMPLE INVESTOR PTY LTD' : 'Sample Investor Pty Ltd';
  return (
    <Stack spacing={2}>
      {values['cert.enabled'] === true ? (
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            borderWidth: 3,
            borderStyle: 'double',
            borderColor: text(values, 'brand.colour'),
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="baseline">
            <Typography
              sx={{ fontWeight: 800, letterSpacing: '0.2em', color: text(values, 'brand.colour') }}
            >
              FIDES
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              No. {certificateNumber(values)}
            </Typography>
          </Stack>
          <Typography variant="h6" sx={{ textAlign: 'center', my: 1.5 }}>
            {text(values, 'cert.title')}
          </Typography>
          <Typography variant="body2" sx={{ textAlign: 'center' }}>
            This certifies that <strong>{holderName}</strong> is the registered holder of{' '}
            <strong>{sampleUnits.toLocaleString('en-AU')}</strong> {text(values, 'class.name')} in{' '}
            {text(values, 'fund.name')}
            {partlyPaid ? `, paid to ${paidText || '__'}% of the issue price` : ', fully paid'}.
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" color="text.secondary">
            {text(values, 'cert.terms')}
          </Typography>
        </Paper>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Certificates are switched off for this class.
        </Typography>
      )}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Holding statement line
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {sampleUnits.toLocaleString('en-AU')} {text(values, 'class.name')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {partlyPaid && !paidText
            ? `Paid __% of ${money(sampleUnits * price)}`
            : partlyPaid
              ? `Paid ${money(sampleUnits * price * (paidPct / 100))} of ${money(sampleUnits * price)} · uncalled ${money(sampleUnits * price * (1 - paidPct / 100))}`
              : `Fully paid · ${money(sampleUnits * price)}`}
        </Typography>
      </Paper>
    </Stack>
  );
}
